import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import {
  Card,
  CardTitle,
  CardSubtitle,
  CardRow,
  CardBody,
  ListWrapper,
  ListItem,
  ListItemText,
  ListItemPrimaryText,
  ListItemSubtext,
  ListItemCheckbox,
  SearchInput,
  Input,
} from 'cdk-radial';
import BodyContainer from '../bodyContainer/BodyContainer';
import { setApiSubscriptionSettings } from '../../../../redux/connectedComponent/dealerApiPreferences/dealerApiPreferences.slice';

import './styles/SelectApiSubscriptionSettings.scss';

const TITLE = 'Configure the APIs for each location';
const CONFIG_TYPES = {
  ENUM: 'enum',
  ENUM_MULTIPLE: 'enum_multiple',
  STRING: 'string',
};

const StyledInput = styled(Input)`
  cursor: pointer;
  svg {
    cursor: pointer;
  }
  width: 100%;
  text-align: left;

  input {
    text-align: left !important;
  }
`;

const DPCardTitle = styled(CardTitle)`
  display: flex;
  gap: 0.25rem;
  align-items: center;
`;

const DPCard = styled(Card)`
  box-sizing: border-box;
  border: 1px solid #b8babd;
  box-shadow: none;
  max-height: 600px;
`;
const DPCardHeaderRow = styled(CardRow)`
  padding: 1rem;
  align-items: center;
  justify-content: space-between;
  height: 4.75rem;
  flex-grow: 0;
  flex-shrink: 0;
  border-bottom: 1px solid #b8babd;
`;
const DPCardHeaderGroup = styled(CardBody)`
  height: 4.75rem;
  border-bottom: 1px solid #b8babd;
  flex-grow: 0;
  flex-shrink: 0;
`;
const DPScrollingList = styled(ListWrapper)`
  overflow: scroll;
`;
const DPColoredListItem = styled(ListItem)`
  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      &,
      &:focus {
        background-color: ${theme.color.primary[200].value};
      }
    `}
`;
const DPTallListItem = styled(DPColoredListItem)`
  height: 4.5rem;
`;

const RequiredField = () => <div title="required">*</div>;
export function SubscriptionSettingsLocationsCard({
  className,
  locations,
  selectedLocation,
  selectLocation,
}) {
  const [searchInput, setSearchInput] = useState('');

  const filteredLocations = useMemo(
    () =>
      locations.filter(
        (loc) =>
          !searchInput ||
          loc.location.toLowerCase().includes(searchInput.toLowerCase())
      ),
    [locations, searchInput]
  );

  return (
    <DPCard className={className} data-testid="location-card">
      <DPCardHeaderRow>
        <CardTitle>Dealership Locations</CardTitle>
        <SearchInput
          name="select-api-subscription-settings-locations-search"
          id="select-api-subscription-settings-locations-search"
          placeholder="Search Locations"
          value={searchInput}
          onChange={setSearchInput}
          isAlwaysExpanded
          style={{ width: '50%' }}
        />
      </DPCardHeaderRow>
      <DPScrollingList>
        {filteredLocations.map((loc) => {
          return (
            <DPTallListItem
              key={loc.entityId}
              onClick={() => selectLocation(loc)}
              isSelected={selectedLocation?.entityId === loc?.entityId}
            >
              <ListItemText>
                <ListItemPrimaryText>{loc.location}</ListItemPrimaryText>
                <ListItemSubtext>{loc.address}</ListItemSubtext>
              </ListItemText>
            </DPTallListItem>
          );
        })}
      </DPScrollingList>
    </DPCard>
  );
}

export function SubscriptionSettingsApiCard({
  selectedLocation,
  apiDetails,
  selectApi,
  selectedApi,
}) {
  const items = apiDetails.map((api) => {
    return { api };
  });

  return (
    <DPCard data-testid="api-card">
      <DPCardHeaderGroup>
        <CardTitle>APIs</CardTitle>
      </DPCardHeaderGroup>
      {selectedLocation ? (
        <DPScrollingList>
          {items.map(({ api }) => (
            <DPColoredListItem
              key={api.id}
              onClick={() => selectApi({ selectedLocation, apiId: api.id })}
              isSelected={selectedApi?.apiId === api.id}
            >
              <ListItemText>
                <ListItemPrimaryText> {api.name}</ListItemPrimaryText>
              </ListItemText>
            </DPColoredListItem>
          ))}
        </DPScrollingList>
      ) : null}
    </DPCard>
  );
}

export function SubscriptionSettingsSelectionCard({
  selectedLocation,
  selectedApi,
  apiOptions,
  setApiSubscriptionSettings,
  selectedDealerApiPreferences,
}) {
  // the backend supports multiple API configurations per location, but the UI only allows one at a time for now so just display the first item in the array to avoid breaking the UI if someone enters multiple configurations into the DB
  const apiConfiguration = apiOptions[0];
  return (
    <DPCard data-testid="settings-card">
      {apiConfiguration && selectedLocation && selectedApi?.apiId ? (
        <DealerOptionsSelectionItem
          apiConfiguration={apiConfiguration}
          key={apiConfiguration.displayName}
          setApiSubscriptionSettings={setApiSubscriptionSettings}
          preferenceSelection={
            selectedDealerApiPreferences?.[selectedLocation.entityId]?.[
              selectedApi.apiId
            ] || {}
          }
        />
      ) : null}
    </DPCard>
  );
}

function DealerOptionsSelectionItem({
  apiConfiguration,
  setApiSubscriptionSettings,
  preferenceSelection,
}) {
  if (apiConfiguration.type === CONFIG_TYPES.ENUM) {
    return (
      <>
        <DPCardHeaderGroup>
          <DPCardTitle>
            <div>{apiConfiguration.displayName}</div>
            {apiConfiguration.required && <RequiredField />}
          </DPCardTitle>
          <CardSubtitle>
            Select one
          </CardSubtitle>
        </DPCardHeaderGroup>
        <DPScrollingList>
          {apiConfiguration.options.map((option) => (
            <ListItem
              key={option}
              onClick={() => {
                setApiSubscriptionSettings({
                  [apiConfiguration.name]: option,
                });
              }}
            >
              <ListItemCheckbox
                checked={preferenceSelection[apiConfiguration.name] === option}
              />
              <ListItemText>
                <ListItemPrimaryText>{option}</ListItemPrimaryText>
              </ListItemText>
            </ListItem>
          ))}
        </DPScrollingList>
      </>
    );
  } else if (apiConfiguration.type === CONFIG_TYPES.ENUM_MULTIPLE) {
    return (
      <>
        <DPCardHeaderGroup>
          <DPCardTitle>
            <div>{apiConfiguration.displayName}</div>
            {apiConfiguration.required && <RequiredField />}
          </DPCardTitle>
          <CardSubtitle>
            Select one or more
          </CardSubtitle>
        </DPCardHeaderGroup>
        <DPScrollingList>
          {apiConfiguration.options.map((option) => (
            <ListItem
              key={option}
              onClick={() => {
                let updatedSelection = preferenceSelection[
                  apiConfiguration.name
                ]
                  ? [...preferenceSelection[apiConfiguration.name]]
                  : [];
                if (updatedSelection.includes(option)) {
                  // remove it
                  updatedSelection = updatedSelection.filter(
                    (item) => item !== option
                  );
                } else {
                  // add it
                  updatedSelection.push(option);
                }
                setApiSubscriptionSettings({
                  [apiConfiguration.name]: updatedSelection,
                });
              }}
            >
              <ListItemCheckbox
                checked={
                  preferenceSelection[apiConfiguration.name] &&
                  Array.isArray(preferenceSelection[apiConfiguration.name]) &&
                  preferenceSelection[apiConfiguration.name].includes(option)
                }
              />
              <ListItemText>
                <ListItemPrimaryText>{option}</ListItemPrimaryText>
              </ListItemText>
            </ListItem>
          ))}
        </DPScrollingList>
      </>
    );
  } else if (apiConfiguration.type === CONFIG_TYPES.STRING) {
    return (
      <>
        <div>
          <DPCardHeaderGroup>
            <DPCardTitle>
            <div>{apiConfiguration.displayName}</div>
            {apiConfiguration.required && <RequiredField />}
            </DPCardTitle>
            <CardSubtitle data-testid="subscription-preference-hint">
              {apiConfiguration.hint || `Enter ${apiConfiguration.displayName}`}
            </CardSubtitle>
          </DPCardHeaderGroup>
          <div style={{ margin: '1rem 1rem 1rem 1rem' }}>
            <StyledInput
              name={apiConfiguration.displayName}
              id={`api-config-${apiConfiguration.name}`}
              labelAlign={'left'}
              placeholder={`Enter ${apiConfiguration.displayName}`}
              value={preferenceSelection[apiConfiguration.name] || ''}
              onChange={(e) => {
                setApiSubscriptionSettings({
                  [apiConfiguration.name]: e.target.value,
                });
              }}
              style={{ margin: '0rem' }}
              maxLength={256}
            />
          </div>
        </div>
      </>
    );
  } else return <div></div>;
}

export default function SelectApiSubscriptionSettings({ disableNextButton }) {
  const dispatch = useDispatch();
  const sysInfoState = useSelector((state) => state.selectDMSSlice);
  const appState = useSelector((state) => state.solutionInfoSlice);
  const dealerApiPreferencesState = useSelector(
    (state) => state.dealerApiPreferencesSlice
  );

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedApi, setSelectedApi] = useState(null);

  useEffect(() => {
    const locations = dealerApiPreferencesState?.availableLocations;
    if (Array.isArray(locations) && locations.length > 0) {
      setSelectedLocation(locations[0]);
    } else {
      setSelectedLocation(null);
    }
  }, [dealerApiPreferencesState?.availableLocations]);

  const [apisThatSupportDealerApiPreferences] = useMemo(() => {
    const solutionInfo = appState.solutionInfo;

    const apiDetails = solutionInfo?.registration_details?.api_details || [];

    const supportedApis = Object.entries(sysInfoState.dealerApiPreferences || {})
      .map(([k, v]) => (v ? apiDetails.find((api) => api.id === k) : null))
      .filter((v) => v);

    return [supportedApis];
  }, [sysInfoState.dealerApiPreferences, appState.solutionInfo]);

  const apiOptionsMap = sysInfoState.dealerApiPreferences;

  // Validate that all required API options are configured for each location
  const canProceed = useMemo(() => {
    const preferences = dealerApiPreferencesState?.preferences;

    if (
      !dealerApiPreferencesState.availableLocations?.length ||
      !apisThatSupportDealerApiPreferences?.length ||
      !preferences
    ) {
      return false;
    }

    for (const location of dealerApiPreferencesState.availableLocations) {
      for (const api of apisThatSupportDealerApiPreferences) {
        const apiOptions = apiOptionsMap[api.id] || [];
        for (const option of apiOptions) {
          if (option.required) {
            const locationPrefs = preferences[location.entityId];
            const apiPrefs = locationPrefs?.[api.id];

            // Check if preferences exist for this location and API
            if (!locationPrefs || !apiPrefs) {
              return false;
            }

            // Check if the specific required option has a valid value
            const optionValue = apiPrefs[option.name];
            
            if (optionValue === undefined || optionValue === null) {
              return false;
            }
            
            if (Array.isArray(optionValue) && optionValue.length === 0) {
              return false;
            }
            
            if (typeof optionValue === 'string' && optionValue.trim() === '') {
              return false;
            }
          }
        }
      }
    }

    return true;
  }, [
    dealerApiPreferencesState.availableLocations,
    dealerApiPreferencesState.preferences,
    apisThatSupportDealerApiPreferences,
    apiOptionsMap,
  ]);

  useEffect(() => {
    disableNextButton?.(!canProceed);
  }, [canProceed, disableNextButton]);

  return (
    <BodyContainer title={TITLE}>
      <div className="select-api-subscription-settings-body">
        <SubscriptionSettingsLocationsCard
          locations={dealerApiPreferencesState.availableLocations}
          selectedLocation={selectedLocation}
          selectLocation={(loc) => setSelectedLocation(loc)}
        />

        <SubscriptionSettingsApiCard
          locations={dealerApiPreferencesState.availableLocations}
          selectedLocation={selectedLocation}
          apiDetails={apisThatSupportDealerApiPreferences}
          selectApi={(api) => setSelectedApi(api)}
          selectedApi={selectedApi}
        />

        <SubscriptionSettingsSelectionCard
          apiOptions={apiOptionsMap[selectedApi?.apiId] || []}
          locations={dealerApiPreferencesState.availableLocations}
          selectedApi={selectedApi}
          selectedLocation={selectedLocation}
          apiDetails={apisThatSupportDealerApiPreferences}
          setApiSubscriptionSettings={(settings) => {
            dispatch(
              setApiSubscriptionSettings({
                orgId: selectedLocation.entityId,
                apiId: selectedApi.apiId,
                settings: settings,
              })
            );
          }}
          selectedDealerApiPreferences={dealerApiPreferencesState?.preferences}
        />
      </div>
    </BodyContainer>
  );
}
